import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layers, Users2, Mail, MessageSquare, ArrowRight, Ticket, Megaphone, ListChecks, Clock, AlertTriangle, UserCheck, ClipboardCheck, BookOpen, RefreshCw, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { fetchSubscribers, fetchContacts, listVerticals, listUsers, crmMyWork, apiClient } from '../../services/api';
import { useAuth, isSuperAdmin } from '../../context/AuthContext';
import BooksMisWidget from '../../components/BooksMisWidget';
import ExpiryRadarTile from '../../components/admin/ExpiryRadarTile';
import { toast } from '../../hooks/use-toast';

const priorityBadge = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-emerald-100 text-emerald-700',
  low: 'bg-gray-100 text-gray-600',
};

const AdminHome = () => {
  const { user } = useAuth();
  const [state, setState] = useState({ subs: 0, contacts: 0, verticals: 0, users: 0 });
  const [work, setWork] = useState({ open_tickets: [], tasks_this_week: [], unread_broadcasts: [], delegated: [], awaiting_approval: [], counts: {} });
  const isStaff = user?.role === 'staff';
  const canSeeCMS = ['super_admin', 'admin', 'content_publisher'].includes(user?.role);

  useEffect(() => {
    crmMyWork().then(setWork).catch(() => {});
    if (!canSeeCMS) return;
    (async () => {
      const [s, c, v] = await Promise.allSettled([fetchSubscribers(), fetchContacts(), listVerticals()]);
      const usersCount = isSuperAdmin(user)
        ? await listUsers().then((r) => r.count).catch(() => 0)
        : 0;
      setState({
        subs: s.status === 'fulfilled' ? s.value.count || 0 : 0,
        contacts: c.status === 'fulfilled' ? c.value.count || 0 : 0,
        verticals: v.status === 'fulfilled' ? v.value.count || 0 : 0,
        users: usersCount,
      });
    })();
  }, [user, canSeeCMS]);

  return (
    <div data-testid="admin-home">
      <div className="mb-8">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Your work</span>
        <h1 className="mt-2 text-4xl lg:text-5xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
          Welcome, {(user?.name || '').split(' ')[0] || 'there'}
        </h1>
        <p className="mt-2 text-sm text-[#0b1a2e]/60">Everything that needs your attention today.</p>
      </div>

      {/* My Work headline counters */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
        <MyWorkCard
          icon={Ticket}
          label="Open tickets"
          value={work.counts.open_tickets || 0}
          to="/admin/crm"
          color="bg-[#0f5c3d]"
        />
        <MyWorkCard
          icon={ListChecks}
          label="My tasks"
          value={work.counts.tasks_this_week || 0}
          to="/admin/calendar"
          color="bg-[#0b1a2e]"
        />
        <MyWorkCard
          icon={UserCheck}
          label="Delegated"
          value={work.counts.delegated || 0}
          to="/admin/crm?view=delegated"
          color="bg-[#3b6e47]"
        />
        <MyWorkCard
          icon={ClipboardCheck}
          label="Awaiting approval"
          value={work.counts.awaiting_approval || 0}
          to="/admin/crm?view=approval"
          color="bg-amber-600"
        />
        <MyWorkCard
          icon={Megaphone}
          label="Broadcasts"
          value={work.counts.unread_broadcasts || 0}
          to="/admin/broadcasts"
          color="bg-orange-500"
        />
      </div>

      {/* Books MIS widget — only renders if the current user has linked a Books tenant */}
      <BooksMisWidget />

      {isSuperAdmin(user) && <ManualRebuildCard />}

      {/* HR Compliance radar — expiring visas, contracts, certs */}
      {(['super_admin', 'admin', 'hr'].includes(user?.role)) && (
        <div className="mb-6"><ExpiryRadarTile /></div>
      )}

      {/* Detail lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
        <div className="bg-white border border-black/5 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0f5c3d]">Your open tickets</div>
            <Link to="/admin/crm" className="text-xs text-[#0b1a2e]/60 hover:text-[#0f5c3d] inline-flex items-center gap-1">All tickets <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {work.open_tickets.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#0b1a2e]/50">Nothing on your plate 🎉</div>
          ) : work.open_tickets.slice(0, 8).map((t) => (
            <Link key={t.id} to="/admin/crm" data-testid="my-open-ticket" className="flex items-center gap-3 py-2 border-t border-black/5 first:border-t-0 hover:bg-[#f7fbf8] rounded px-1">
              <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${priorityBadge[t.priority] || priorityBadge.medium}`}>{t.priority}</span>
              <span className="font-mono text-[11px] text-[#0f5c3d]">{t.ticket_no}</span>
              <span className="flex-1 text-sm text-[#0b1a2e] truncate">{t.title}</span>
            </Link>
          ))}
        </div>

        <div className="bg-white border border-black/5 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0f5c3d]">Due this week</div>
            <Link to="/admin/crm" className="text-xs text-[#0b1a2e]/60 hover:text-[#0f5c3d] inline-flex items-center gap-1">All tasks <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {work.tasks_this_week.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#0b1a2e]/50">No tasks due in the next 7 days.</div>
          ) : work.tasks_this_week.slice(0, 8).map((t) => {
            const overdue = new Date(t.due_date) < new Date();
            return (
              <div key={t.id} data-testid="my-task" className="flex items-center gap-3 py-2 border-t border-black/5 first:border-t-0">
                {overdue ? <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> : <Clock className="w-3.5 h-3.5 text-[#0b1a2e]/40" />}
                <span className="flex-1 text-sm text-[#0b1a2e] truncate">{t.title}</span>
                <span className={`text-[11px] font-mono ${overdue ? 'text-red-600 font-semibold' : 'text-[#0b1a2e]/60'}`}>{String(t.due_date).slice(0, 10)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {work.unread_broadcasts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-2 text-amber-900">
            <Megaphone className="w-4 h-4" />
            <div className="text-xs font-semibold uppercase tracking-widest">Unread broadcasts</div>
          </div>
          {work.unread_broadcasts.slice(0, 3).map((b) => (
            <Link key={b.ref_id} to="/admin/broadcasts" className="block py-2 border-t border-amber-200 first:border-t-0 hover:bg-amber-100/50 rounded px-1">
              <div className="text-sm font-medium text-[#0b1a2e]">{b.subject}</div>
              <div className="text-xs text-[#0b1a2e]/60 truncate">{b.snippet}</div>
            </Link>
          ))}
        </div>
      )}

      {/* Delegated + Awaiting approval — visible when there's anything to show */}
      {(work.delegated?.length > 0 || work.awaiting_approval?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
          {work.delegated?.length > 0 && (
            <div className="bg-white border border-black/5 rounded-2xl p-5" data-testid="delegated-by-me">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold uppercase tracking-widest text-[#3b6e47]">Delegated by me</div>
                <Link to="/admin/crm" className="text-xs text-[#0b1a2e]/60 hover:text-[#0f5c3d] inline-flex items-center gap-1">All <ArrowRight className="w-3 h-3" /></Link>
              </div>
              {work.delegated.slice(0, 6).map((t) => (
                <Link key={t.id} to={`/admin/calendar?task=${t.id}`} className="flex items-center gap-3 py-2 border-t border-black/5 first:border-t-0 hover:bg-[#f7fbf8] rounded px-1">
                  <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${priorityBadge[t.priority] || priorityBadge.medium}`}>{t.priority}</span>
                  <span className="flex-1 text-sm text-[#0b1a2e] truncate">{t.title}</span>
                  <span className="text-[11px] font-mono text-[#0b1a2e]/60">{String(t.status || '').replace('_', ' ')}</span>
                </Link>
              ))}
            </div>
          )}
          {work.awaiting_approval?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="awaiting-approval">
              <div className="flex items-center gap-2 mb-3 text-amber-900">
                <ClipboardCheck className="w-4 h-4" />
                <div className="text-xs font-semibold uppercase tracking-widest">Awaiting my approval</div>
              </div>
              {work.awaiting_approval.slice(0, 6).map((t) => (
                <Link key={t.id} to={`/admin/calendar?task=${t.id}`} className="flex items-center gap-3 py-2 border-t border-amber-200 first:border-t-0 hover:bg-amber-100/50 rounded px-1">
                  <span className="flex-1 text-sm text-[#0b1a2e] truncate">{t.title}</span>
                  <span className="text-[11px] font-mono text-[#0b1a2e]/60">{t.due_date ? String(t.due_date).slice(0, 10) : ''}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CMS stats — only for content publishers and above */}
      {canSeeCMS && (
        <>
          <div className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase mt-6 mb-3">Portal</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Layers} label="Business Verticals" value={state.verticals} to="/admin/verticals" />
            <StatCard icon={Mail} label="Subscribers" value={state.subs} to="/admin/inbox" />
            <StatCard icon={MessageSquare} label="Contact messages" value={state.contacts} to="/admin/inbox" />
            {isSuperAdmin(user) && (
              <StatCard icon={Users2} label="Portal users" value={state.users} to="/admin/users" />
            )}
          </div>
        </>
      )}
    </div>
  );
};

const MyWorkCard = ({ icon: Icon, label, value, to, color }) => (
  <Link to={to} className="bg-white border border-black/5 rounded-2xl p-5 hover:shadow-md transition flex items-center gap-4">
    <div className={`w-11 h-11 rounded-full ${color} text-white flex items-center justify-center`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-3xl font-bold text-[#0b1a2e]">{value}</div>
      <div className="text-xs text-[#0b1a2e]/60 uppercase tracking-wider">{label}</div>
    </div>
  </Link>
);

const StatCard = ({ icon: Icon, label, value, to }) => (
  <Link to={to} className="bg-white border border-black/5 rounded-2xl p-5 hover:shadow-md transition">
    <div className="flex items-center gap-2 text-[#0b1a2e]/60 mb-2">
      <Icon className="w-4 h-4" />
      <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
    </div>
    <div className="text-3xl font-medium text-[#0b1a2e]">{value}</div>
  </Link>
);

const STAGE_LABEL = {
  running_screenshots: 'Capturing screenshots (Playwright)…',
  running_build: 'Assembling PDF (reportlab)…',
  success: 'Manual rebuilt successfully',
  failed: 'Rebuild failed',
  idle: 'Idle',
};

const ManualRebuildCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await apiClient.get('/admin/manual/status');
      setStatus(data);
    } catch (_) {
      /* silent */
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (status && ['running_screenshots', 'running_build'].includes(status.status)) {
      const iv = setInterval(refresh, 4000);
      return () => clearInterval(iv);
    }
    return undefined;
  }, [status?.status]);

  const trigger = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post('/admin/manual/rebuild');
      if (!data.ok) {
        toast({ title: data.message || 'Rebuild already running' });
      } else {
        toast({ title: 'Manual rebuild started', description: 'Screenshots + PDF are being regenerated. This usually takes 60–90 seconds.' });
      }
      setTimeout(refresh, 500);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed to start rebuild', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const running = ['running_screenshots', 'running_build'].includes(status?.status);
  const failed = status?.status === 'failed';
  const success = status?.status === 'success';
  const pdfMod = status?.pdf_last_modified ? new Date(status.pdf_last_modified).toLocaleString() : '—';
  const pdfKb = status?.pdf_bytes ? `${(status.pdf_bytes / 1024).toFixed(0)} KB` : '—';

  return (
    <div className="bg-white border border-black/5 rounded-2xl p-5 mb-10" data-testid="manual-rebuild-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-[#0f5c3d] text-white flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0f5c3d]">HR &amp; Payroll manual</div>
            <div className="text-sm text-[#0b1a2e] mt-0.5">
              Regenerate the training PDF with fresh screenshots on demand.
            </div>
            <div className="text-[11px] text-[#0b1a2e]/50 mt-1">
              Last built: <span className="font-medium">{pdfMod}</span> · Size: <span className="font-medium">{pdfKb}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {status?.pdf_url && (
            <a
              href={status.pdf_url}
              target="_blank"
              rel="noreferrer"
              data-testid="manual-download-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#0f5c3d]/25 text-[#0f5c3d] text-xs font-medium hover:bg-[#f7fbf8]"
            >
              <BookOpen className="w-3.5 h-3.5" /> Open manual
            </a>
          )}
          <button
            onClick={trigger}
            disabled={busy || running}
            data-testid="manual-rebuild-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold disabled:opacity-60 hover:bg-[#0b4a30]"
          >
            {running || busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {running ? 'Rebuilding…' : 'Rebuild manual'}
          </button>
        </div>
      </div>
      {(running || failed || success) && (
        <div className={`mt-3 rounded-lg px-3 py-2 text-xs flex items-center gap-2 ${
          failed ? 'bg-red-50 text-red-700 border border-red-200'
            : success ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-slate-50 text-slate-700 border border-slate-200'
        }`}>
          {failed ? <XCircle className="w-3.5 h-3.5" /> : success ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <span className="font-medium">{STAGE_LABEL[status.status] || status.status}</span>
          {status.stage && <span className="text-[#0b1a2e]/50">· stage: {status.stage}</span>}
          {status.returncode !== undefined && <span className="text-[#0b1a2e]/50">· rc: {status.returncode}</span>}
        </div>
      )}
    </div>
  );
};

export default AdminHome;
